/**
 * Webtech HR Engine™ - Employee Skills & Gap Analysis
 * Profil de compétences individuel avec analyse des écarts
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  Brain,
  Target,
  TrendingUp,
  TrendingDown,
  Minus,
  Award,
  AlertTriangle,
  CheckCircle2,
  User,
  Briefcase,
  Loader2,
  ChevronRight,
  Plus,
  BarChart3,
  Sparkles,
  GraduationCap,
  Calendar,
  BookOpen,
  Users,
  Video,
  FileText,
  Trash2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';

// Types d'actions de développement
const ACTION_TYPES = [
  { value: 'formation', label: 'Formation', icon: GraduationCap, description: 'Cours ou module de formation' },
  { value: 'coaching', label: 'Coaching', icon: Users, description: 'Accompagnement personnalisé' },
  { value: 'practice', label: 'Mise en pratique', icon: Target, description: 'Exercice concret sur le terrain' },
  { value: 'reading', label: 'Lecture/Documentation', icon: BookOpen, description: 'Ressources à consulter' },
  { value: 'elearning', label: 'E-learning', icon: Video, description: 'Formation en ligne' },
  { value: 'certification', label: 'Certification', icon: Award, description: 'Passage de certification' },
];

const SKILL_LEVELS = {
  1: { label: 'Débutant', color: 'bg-slate-100 text-slate-700' },
  2: { label: 'Intermédiaire', color: 'bg-blue-100 text-blue-700' },
  3: { label: 'Confirmé', color: 'bg-green-100 text-green-700' },
  4: { label: 'Avancé', color: 'bg-purple-100 text-purple-700' },
  5: { label: 'Expert', color: 'bg-orange-100 text-orange-700' },
};

const IMPORTANCE_COLORS = {
  low: 'bg-slate-100 text-slate-600',
  medium: 'bg-yellow-100 text-yellow-700',
  critical: 'bg-red-100 text-red-700',
};

export default function EmployeeSkillsPage() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const companyId = user?.companyId;
  
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [gapAnalysis, setGapAnalysis] = useState(null);
  const [developmentPlans, setDevelopmentPlans] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Create plan dialog states
  const [createPlanOpen, setCreatePlanOpen] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [planActions, setPlanActions] = useState([]);
  const [planNotes, setPlanNotes] = useState('');
  const [planDeadline, setPlanDeadline] = useState('');
  const [targetLevel, setTargetLevel] = useState(3);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState(null);
  
  // New action form
  const [newActionTitle, setNewActionTitle] = useState('');
  const [newActionType, setNewActionType] = useState('formation');
  const [newActionDeadline, setNewActionDeadline] = useState('');
  
  // Delete plan confirmation
  const [deletePlanId, setDeletePlanId] = useState(null);
  const [deletingPlan, setDeletingPlan] = useState(false);

  // Use logged-in user's employee ID if none provided
  const targetEmployeeId = employeeId || user?.employeeId;
  const isManager = user?.role === 'Manager' || user?.role === 'Admin' || user?.role === 'SuperAdmin';

  useEffect(() => {
    if (companyId && targetEmployeeId) {
      loadData();
    }
  }, [companyId, targetEmployeeId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [gapRes, plansRes] = await Promise.all([
        api.get(`/skills/gap-analysis/${targetEmployeeId}?company_id=${companyId}`),
        api.get(`/skills-intelligence/upskilling-plans?company_id=${companyId}&employee_id=${targetEmployeeId}`)
      ]);
      
      setGapAnalysis(gapRes.data);
      setEmployee(gapRes.data.employee);
      setDevelopmentPlans(plansRes.data.plans || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const getGapIcon = (gap) => {
    if (gap < 0) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (gap === 0) return <Minus className="h-4 w-4 text-blue-500" />;
    return <TrendingDown className="h-4 w-4 text-red-500" />;
  };

  const getGapColor = (gap) => {
    if (gap < 0) return 'text-green-600 bg-green-50';
    if (gap === 0) return 'text-blue-600 bg-blue-50';
    if (gap <= 1) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getReadinessColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Get skills with gaps for selection
  const skillsWithGaps = (gapAnalysis?.gaps || []).filter(g => g.gap > 0);

  // Open create plan dialog
  const handleOpenCreatePlan = (preSelectedSkill = null) => {
    if (preSelectedSkill) {
      setSelectedSkill(preSelectedSkill);
      setTargetLevel(preSelectedSkill.requiredLevel);
    } else {
      setSelectedSkill(null);
      setTargetLevel(3);
    }
    setPlanActions([]);
    setPlanNotes('');
    setPlanDeadline(new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    setNewActionTitle('');
    setNewActionType('formation');
    setNewActionDeadline(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    setCreatePlanOpen(true);
  };

  // Add a new action to the plan
  const handleAddAction = () => {
    if (!newActionTitle.trim()) {
      toast.error('Veuillez saisir un titre pour l\'action');
      return;
    }
    
    const typeIcons = {
      formation: '📚',
      coaching: '👥',
      projet: '💼',
      lecture: '📖',
      certification: '✅',
      autre: '📌'
    };
    
    const newAction = {
      id: `action-${Date.now()}`,
      type: newActionType,
      title: `${typeIcons[newActionType] || '📌'} ${newActionTitle}`,
      description: '',
      deadline: newActionDeadline ? new Date(newActionDeadline).toISOString() : null,
      status: 'pending',
      resources: []
    };
    
    setPlanActions(prev => [...prev, newAction]);
    setNewActionTitle('');
    setNewActionDeadline(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  };

  // Remove an action from the plan
  const handleRemoveAction = (actionId) => {
    setPlanActions(prev => prev.filter(a => a.id !== actionId));
  };

  // Move action up/down
  const handleMoveAction = (index, direction) => {
    const newActions = [...planActions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < newActions.length) {
      [newActions[index], newActions[newIndex]] = [newActions[newIndex], newActions[index]];
      setPlanActions(newActions);
    }
  };

  // Manager validates an action
  const handleValidateAction = async (planId, actionIndex) => {
    try {
      await api.put(`/skills-intelligence/upskilling-plans/${planId}/action/${actionIndex}`, {
        status: 'completed',
        completedAt: new Date().toISOString(),
        completedBy: 'manager'
      });
      toast.success('Action validée !');
      await loadData();
    } catch (error) {
      console.error('Error validating action:', error);
      toast.error('Erreur lors de la validation');
    }
  };

  // Manager rejects an action (sends back to pending)
  const handleRejectAction = async (planId, actionIndex) => {
    try {
      await api.put(`/skills-intelligence/upskilling-plans/${planId}/action/${actionIndex}`, {
        status: 'pending',
        comment: 'Refusé par le manager - veuillez refaire l\'action'
      });
      toast.info('Action renvoyée à l\'employé');
      await loadData();
    } catch (error) {
      console.error('Error rejecting action:', error);
      toast.error('Erreur lors du refus');
    }
  };

  // Manager deletes a plan
  const handleDeletePlan = async () => {
    if (!deletePlanId) return;
    
    setDeletingPlan(true);
    try {
      await api.delete(`/skills-intelligence/upskilling-plans/${deletePlanId}?company_id=${companyId}`);
      toast.success('Plan supprimé avec succès');
      setDeletePlanId(null);
      await loadData();
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error('Erreur lors de la suppression du plan');
    } finally {
      setDeletingPlan(false);
    }
  };

  // Create plan with manager-defined actions
  const handleCreatePlan = async () => {
    if (!selectedSkill) {
      toast.error('Sélectionnez une compétence');
      return;
    }
    
    if (planActions.length === 0) {
      toast.error('Ajoutez au moins une action au plan');
      return;
    }
    
    setCreatingPlan(true);
    
    try {
      const planData = {
        employeeId: targetEmployeeId,
        skillId: selectedSkill.skillId,
        skillName: selectedSkill.skillName,
        currentLevel: selectedSkill.currentLevel,
        targetLevel: targetLevel,
        actions: planActions.map(a => ({
          type: a.type,
          title: a.title,
          description: a.description || '',
          deadline: a.deadline,
          resources: a.resources || []
        })),
        notes: planNotes || `Plan de développement pour ${selectedSkill.skillName}`,
        deadline: planDeadline ? new Date(planDeadline).toISOString() : new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString()
      };
      
      await api.post(`/skills-intelligence/upskilling-plans?company_id=${companyId}&created_by=${user.id}`, planData);
      
      toast.success(`Plan de développement créé pour ${selectedSkill.skillName}`);
      setCreatePlanOpen(false);
      
      // Reload data
      await loadData();
      setActiveTab('plans');
      
    } catch (error) {
      console.error('Error creating plan:', error);
      toast.error('Erreur lors de la création du plan');
    } finally {
      setCreatingPlan(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Profil de compétences">
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  if (!gapAnalysis?.hasJobProfile) {
    return (
      <DashboardLayout title="Profil de compétences">
        <div className="space-y-6">
          <Button variant="ghost" className="-ml-2" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          
          <Card className="py-12">
            <CardContent className="text-center">
              <AlertTriangle className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucun profil de poste assigné</h3>
              <p className="text-slate-500 mb-6 max-w-md mx-auto">
                {employee?.firstName} {employee?.lastName} n'a pas de profil de poste assigné.
                L'analyse des écarts nécessite un profil de compétences pour comparaison.
              </p>
              <Button onClick={() => navigate('/skills/profiles')}>
                <Briefcase className="h-4 w-4 mr-2" />
                Gérer les profils de poste
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const { summary, gaps, jobProfile } = gapAnalysis;

  return (
    <DashboardLayout title="Profil de compétences">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-webtech-blue/10 flex items-center justify-center">
                <User className="h-6 w-6 text-webtech-blue" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">
                  {employee?.firstName} {employee?.lastName}
                </h1>
                <p className="text-slate-500">{employee?.position}</p>
              </div>
            </div>
          </div>
          
          {isManager && gapAnalysis?.gaps?.filter(g => g.gap > 0).length > 0 && (
            <div className="flex gap-2">
              <Button onClick={handleOpenCreatePlan} className="bg-purple-600 hover:bg-purple-700">
                <Sparkles className="h-4 w-4 mr-2" />
                Créer un plan intelligent
              </Button>
            </div>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center" title="Score basé sur TOUTES les compétences requises par le profil de poste">
                <div className={`text-3xl font-bold ${getReadinessColor(summary.readinessScore)}`}>
                  {summary.readinessScore}%
                </div>
                <p className="text-sm text-slate-500 mt-1">Score de préparation</p>
                <p className="text-xs text-slate-400">(vs profil de poste)</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900">{summary.totalSkills}</div>
                <p className="text-sm text-slate-500 mt-1">Compétences requises</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{summary.skillsMet + summary.skillsAbove}</div>
                <p className="text-sm text-slate-500 mt-1">Atteintes ou dépassées</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-600">{summary.skillsWithGap}</div>
                <p className="text-sm text-slate-500 mt-1">Écarts à combler</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600">{summary.criticalGaps}</div>
                <p className="text-sm text-slate-500 mt-1">Écarts critiques</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Job Profile Info */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Briefcase className="h-5 w-5 text-webtech-blue" />
                <div>
                  <p className="font-medium text-slate-900">Profil de poste : {jobProfile.name}</p>
                  <p className="text-sm text-slate-500">{jobProfile.totalSkills} compétences requises</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate('/skills/profiles')}>
                Voir les profils
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="gaps">Analyse des écarts</TabsTrigger>
            <TabsTrigger value="plans">Plans de développement ({developmentPlans.length})</TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-4">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Skills with Gaps */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    Compétences à développer
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {gaps.filter(g => g.status === 'gap').slice(0, 5).map(gap => (
                      <div key={gap.skillId} className="flex items-center justify-between p-2 rounded-lg bg-red-50">
                        <div>
                          <p className="font-medium text-slate-900">{gap.skillName}</p>
                          <p className="text-xs text-slate-500">{gap.category}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-red-600 font-medium">-{gap.gap}</span>
                          {gap.importance === 'critical' && (
                            <Badge className="bg-red-100 text-red-700">Critique</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                    {gaps.filter(g => g.status === 'gap').length === 0 && (
                      <p className="text-center text-slate-500 py-4">Aucun écart détecté</p>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Strong Skills */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    Points forts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {gaps.filter(g => g.status === 'above' || g.status === 'met').slice(0, 5).map(gap => (
                      <div key={gap.skillId} className="flex items-center justify-between p-2 rounded-lg bg-green-50">
                        <div>
                          <p className="font-medium text-slate-900">{gap.skillName}</p>
                          <p className="text-xs text-slate-500">{gap.category}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={SKILL_LEVELS[Math.round(gap.currentLevel)]?.color}>
                            Niveau {Math.round(gap.currentLevel)}
                          </Badge>
                          {gap.status === 'above' && (
                            <TrendingUp className="h-4 w-4 text-green-500" />
                          )}
                        </div>
                      </div>
                    ))}
                    {gaps.filter(g => g.status !== 'gap').length === 0 && (
                      <p className="text-center text-slate-500 py-4">Aucune compétence validée</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Gap Analysis Tab */}
          <TabsContent value="gaps" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Analyse détaillée des écarts</CardTitle>
                <CardDescription>
                  Comparaison entre le niveau actuel et le niveau requis pour chaque compétence
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {gaps.map(gap => (
                    <div 
                      key={gap.skillId} 
                      className={`p-4 rounded-lg border ${gap.status === 'gap' ? 'border-red-200 bg-red-50/50' : gap.status === 'above' ? 'border-green-200 bg-green-50/50' : 'border-blue-200 bg-blue-50/50'}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-slate-900">{gap.skillName}</h4>
                            <Badge className={IMPORTANCE_COLORS[gap.importance]}>
                              {gap.importance === 'critical' ? 'Critique' : gap.importance === 'medium' ? 'Moyen' : 'Faible'}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500">{gap.category} → {gap.subcategory}</p>
                        </div>
                        <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${getGapColor(gap.gap)}`}>
                          {getGapIcon(gap.gap)}
                          <span className="font-medium">
                            {gap.gap > 0 ? `-${gap.gap}` : gap.gap < 0 ? `+${Math.abs(gap.gap)}` : '='}
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Niveau requis</p>
                          <p className="font-medium">{gap.requiredLevel} - {SKILL_LEVELS[gap.requiredLevel]?.label}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Auto-évaluation</p>
                          <p className="font-medium">{gap.selfLevel || '-'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Éval. manager</p>
                          <p className="font-medium">{gap.managerLevel || '-'}</p>
                        </div>
                      </div>
                      
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-slate-500 mb-1">
                          <span>Niveau actuel: {gap.currentLevel}</span>
                          <span>Requis: {gap.requiredLevel}</span>
                        </div>
                        <div className="relative h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`absolute h-full rounded-full ${gap.status === 'gap' ? 'bg-red-500' : gap.status === 'above' ? 'bg-green-500' : 'bg-blue-500'}`}
                            style={{ width: `${Math.min(100, (gap.currentLevel / gap.requiredLevel) * 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Development Plans Tab */}
          <TabsContent value="plans" className="mt-4">
            {developmentPlans.length === 0 ? (
              <Card className="py-12">
                <CardContent className="text-center">
                  <Target className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucun plan de développement</h3>
                  <p className="text-slate-500 mb-6">Créez un plan pour suivre la progression.</p>
                  {isManager && gapAnalysis?.gaps?.filter(g => g.gap > 0).length > 0 ? (
                    <Button onClick={handleOpenCreatePlan}>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Créer un plan intelligent
                    </Button>
                  ) : isManager ? (
                    <p className="text-sm text-green-600">Toutes les compétences sont au niveau requis</p>
                  ) : null}
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {developmentPlans.map(plan => (
                  <Card key={plan.id}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="h-10 w-10 rounded-lg bg-webtech-blue/10 flex items-center justify-center">
                            <Target className="h-5 w-5 text-webtech-blue" />
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-900">{plan.skillName || plan.skill?.name || 'Compétence'}</h4>
                            <p className="text-sm text-slate-500">
                              Objectif: Niveau {plan.currentLevel} → {plan.targetLevel}
                            </p>
                            {plan.actions?.filter(a => a.status === 'pending_validation').length > 0 && (
                              <Badge className="bg-amber-100 text-amber-700 mt-1">
                                {plan.actions.filter(a => a.status === 'pending_validation').length} action(s) à valider
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm font-medium text-slate-900">{plan.progress || 0}%</p>
                            <p className="text-xs text-slate-500">Progression</p>
                          </div>
                          <Progress value={plan.progress || 0} className="w-24 h-2" />
                          <Badge className={plan.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                            {plan.status === 'active' ? 'Actif' : 'Complété'}
                          </Badge>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setExpandedPlan(expandedPlan === plan.id ? null : plan.id)}
                          >
                            {expandedPlan === plan.id ? 'Masquer' : 'Voir actions'}
                          </Button>
                          {isManager && (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setDeletePlanId(plan.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Expanded actions view for manager */}
                      {expandedPlan === plan.id && plan.actions && (
                        <div className="mt-4 pt-4 border-t">
                          <p className="text-sm font-medium text-slate-700 mb-3">Actions du plan :</p>
                          <div className="space-y-2">
                            {plan.actions.map((action, idx) => (
                              <div 
                                key={idx}
                                className={`p-3 rounded-lg border flex items-center justify-between ${
                                  action.status === 'completed' 
                                    ? 'bg-green-50 border-green-200' 
                                    : action.status === 'pending_validation'
                                    ? 'bg-amber-50 border-amber-200'
                                    : 'bg-slate-50 border-slate-200'
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`h-6 w-6 rounded-full flex items-center justify-center ${
                                    action.status === 'completed' 
                                      ? 'bg-green-500 text-white' 
                                      : action.status === 'pending_validation'
                                      ? 'bg-amber-500 text-white'
                                      : 'bg-slate-200'
                                  }`}>
                                    {action.status === 'completed' ? '✓' : action.status === 'pending_validation' ? '!' : idx + 1}
                                  </div>
                                  <div>
                                    <p className={`font-medium ${action.status === 'completed' ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                                      {action.title}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                      {action.deadline && `Échéance: ${new Date(action.deadline).toLocaleDateString('fr-FR')}`}
                                    </p>
                                  </div>
                                </div>
                                
                                {/* Manager action buttons */}
                                {action.status === 'pending_validation' ? (
                                  <div className="flex gap-2">
                                    <Button 
                                      size="sm" 
                                      className="bg-green-600 hover:bg-green-700"
                                      onClick={() => handleValidateAction(plan.id, idx)}
                                    >
                                      <CheckCircle2 className="h-4 w-4 mr-1" />
                                      Valider
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="text-red-600 border-red-200"
                                      onClick={() => handleRejectAction(plan.id, idx)}
                                    >
                                      Refuser
                                    </Button>
                                  </div>
                                ) : action.status === 'completed' ? (
                                  <Badge className="bg-green-100 text-green-700">Validé</Badge>
                                ) : (
                                  <Badge variant="outline">En attente</Badge>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Create Plan Dialog - Manual Actions Entry */}
      <Dialog open={createPlanOpen} onOpenChange={setCreatePlanOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              Créer un plan de développement
            </DialogTitle>
            <DialogDescription>
              Définissez les actions de développement pour {employee?.firstName} {employee?.lastName}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Step 1: Select Skill */}
            <div>
              <Label className="text-base font-medium mb-3 block">
                1. Sélectionnez la compétence à développer
              </Label>
              <div className="grid gap-2">
                {skillsWithGaps.map((skill) => (
                  <div 
                    key={skill.skillId}
                    onClick={() => {
                      setSelectedSkill(skill);
                      setTargetLevel(skill.requiredLevel);
                    }}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedSkill?.skillId === skill.skillId 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                          selectedSkill?.skillId === skill.skillId 
                            ? 'border-purple-500 bg-purple-500' 
                            : 'border-slate-300'
                        }`}>
                          {selectedSkill?.skillId === skill.skillId && (
                            <div className="h-2 w-2 rounded-full bg-white" />
                          )}
                        </div>
                        <div>
                          <span className="font-medium text-slate-900">{skill.skillName}</span>
                          {skill.importance === 'critical' && (
                            <Badge className="ml-2 bg-red-100 text-red-700 text-xs">Critique</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-slate-500">Niveau {skill.currentLevel} → {skill.requiredLevel}</span>
                        <Badge variant="outline" className="text-red-600 border-red-200">
                          Écart: -{skill.gap}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
                {skillsWithGaps.length === 0 && (
                  <div className="text-center py-6 text-slate-500">
                    <CheckCircle2 className="h-10 w-10 mx-auto text-green-500 mb-2" />
                    <p>Toutes les compétences sont au niveau requis</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Step 2: Target Level */}
            {selectedSkill && (
              <div>
                <Label className="text-base font-medium mb-3 block">
                  2. Niveau cible
                </Label>
                <div className="flex items-center gap-4">
                  <select
                    value={targetLevel}
                    onChange={(e) => setTargetLevel(parseInt(e.target.value))}
                    className="h-10 px-3 rounded-md border border-slate-200 text-sm"
                  >
                    {[2, 3, 4, 5].filter(l => l > selectedSkill.currentLevel).map(level => (
                      <option key={level} value={level}>
                        Niveau {level} - {SKILL_LEVELS[level]?.label}
                      </option>
                    ))}
                  </select>
                  <span className="text-sm text-slate-500">
                    (Actuel: {selectedSkill.currentLevel} - {SKILL_LEVELS[selectedSkill.currentLevel]?.label})
                  </span>
                </div>
              </div>
            )}
            
            {/* Step 3: Manual Actions */}
            {selectedSkill && (
              <div>
                <Label className="text-base font-medium mb-3 block">
                  3. Définissez les actions à réaliser
                </Label>
                
                {/* Add new action form */}
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 mb-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-2">
                      <Label className="text-sm text-slate-600 mb-1 block">Titre de l'action</Label>
                      <Input
                        placeholder="Ex: Formation Excel avancé, Coaching avec Marie..."
                        value={newActionTitle}
                        onChange={(e) => setNewActionTitle(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddAction()}
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 mb-1 block">Type</Label>
                      <select
                        value={newActionType}
                        onChange={(e) => setNewActionType(e.target.value)}
                        className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm"
                      >
                        {ACTION_TYPES.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                    <div>
                      <Label className="text-sm text-slate-600 mb-1 block">Échéance</Label>
                      <Input
                        type="date"
                        value={newActionDeadline}
                        onChange={(e) => setNewActionDeadline(e.target.value)}
                      />
                    </div>
                    <div className="md:col-span-2 flex items-end">
                      <Button 
                        type="button"
                        onClick={handleAddAction}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Ajouter l'action
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Actions list */}
                {planActions.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700 mb-2">
                      Actions ajoutées ({planActions.length})
                    </p>
                    {planActions.map((action, index) => {
                      const ActionIcon = ACTION_TYPES.find(t => t.value === action.type)?.icon || FileText;
                      return (
                        <div 
                          key={action.id}
                          className="p-3 bg-white rounded-lg border border-slate-200 flex items-center justify-between group"
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex flex-col gap-1">
                              <button 
                                onClick={() => handleMoveAction(index, 'up')}
                                disabled={index === 0}
                                className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
                              >
                                ▲
                              </button>
                              <button 
                                onClick={() => handleMoveAction(index, 'down')}
                                disabled={index === planActions.length - 1}
                                className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
                              >
                                ▼
                              </button>
                            </div>
                            <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                              <ActionIcon className="h-4 w-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{action.title}</p>
                              <p className="text-xs text-slate-500">
                                {ACTION_TYPES.find(t => t.value === action.type)?.label}
                                {action.deadline && ` • Échéance: ${new Date(action.deadline).toLocaleDateString('fr-FR')}`}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveAction(action.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            Supprimer
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6 border-2 border-dashed border-slate-200 rounded-lg">
                    <FileText className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-500 text-sm">Aucune action ajoutée</p>
                    <p className="text-slate-400 text-xs">Utilisez le formulaire ci-dessus pour ajouter des actions</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Plan deadline */}
            {selectedSkill && (
              <div>
                <Label className="text-base font-medium mb-3 block">
                  4. Échéance globale du plan
                </Label>
                <Input
                  type="date"
                  value={planDeadline}
                  onChange={(e) => setPlanDeadline(e.target.value)}
                  className="w-48"
                />
              </div>
            )}
            
            {/* Notes */}
            {selectedSkill && (
              <div>
                <Label htmlFor="plan-notes" className="text-base font-medium mb-2 block">
                  5. Notes pour l'employé (optionnel)
                </Label>
                <Textarea
                  id="plan-notes"
                  placeholder="Ajoutez des instructions ou conseils spécifiques pour l'employé..."
                  value={planNotes}
                  onChange={(e) => setPlanNotes(e.target.value)}
                  rows={2}
                />
              </div>
            )}
            
            {/* Summary */}
            {selectedSkill && planActions.length > 0 && (
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                  <span className="font-medium text-purple-900">Résumé du plan</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-purple-600">Compétence</p>
                    <p className="font-bold text-purple-900">{selectedSkill.skillName}</p>
                  </div>
                  <div>
                    <p className="text-purple-600">Objectif</p>
                    <p className="font-bold text-purple-900">
                      Niveau {selectedSkill.currentLevel} → {targetLevel}
                    </p>
                  </div>
                  <div>
                    <p className="text-purple-600">Actions</p>
                    <p className="font-bold text-purple-900">{planActions.length}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreatePlanOpen(false)} disabled={creatingPlan}>
              Annuler
            </Button>
            <Button 
              onClick={handleCreatePlan} 
              disabled={creatingPlan || !selectedSkill || planActions.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {creatingPlan ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Création en cours...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Créer le plan
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Plan Confirmation Dialog */}
      <Dialog open={!!deletePlanId} onOpenChange={(open) => !open && setDeletePlanId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Supprimer le plan
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer ce plan de développement ? 
              Cette action est irréversible et toutes les actions associées seront perdues.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setDeletePlanId(null)}
              disabled={deletingPlan}
            >
              Annuler
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeletePlan}
              disabled={deletingPlan}
            >
              {deletingPlan ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
